import { LucideBuilding } from 'lucide-react'
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/components/ui'
import { STATUSES, TYPES } from '~/constants'
import { cn, fmtNumberAbbr, getPropertyTypeClassName } from '~/lib'
import type { ClientLabel, Listing } from '~/models'

export function ClientLabelBadge({ label }: { label: ClientLabel }) {
  const { name, description } = label
  const badge = (
    <Badge className="bg-[#059669] text-base text-white hover:bg-[#059669]">
      {name}
    </Badge>
  )
  if (description) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger className="cursor-pointer">{badge}</TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="max-w-md py-2 text-base leading-tight">
            <span>{description}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
  return badge
}

export function ListingLabels({ listing }: { listing: Listing }) {
  if (!listing.meta) {
    return null
  }
  const filters = listing.meta!
  const children: { child: string | React.ReactNode; content?: string }[] = []

  if (filters.price) {
    children.push({
      child: `$${fmtNumberAbbr(filters.price)}`,
      content: `Price: $${filters.price.toLocaleString()}`,
    })
  }
  if (filters.cap_rate) {
    children.push({
      child: `${filters.cap_rate}%`,
      content: `Cap Rate: ${filters.cap_rate}%`,
    })
  }
  if (filters.acres) {
    children.push({
      child: `${filters.acres.toLocaleString()} ac`,
      content: `Acres: ${filters.acres.toLocaleString()}`,
    })
  }
  if (listing.type.toLowerCase().includes('lease')) {
    if (listing.status != 'leased')
      if (
        filters.min_lease_rate &&
        filters.max_lease_rate &&
        filters.min_lease_rate !== filters.max_lease_rate
      ) {
        children.push({
          child: `$${filters.min_lease_rate} - $${filters.max_lease_rate} SF/year`,
          content: `Rate: $${filters.min_lease_rate} - $${filters.max_lease_rate} SF/year`,
        })
      } else if (filters.min_lease_rate) {
        children.push({
          child: `$${filters.min_lease_rate} SF/year`,
          content: `Rate: $${filters.min_lease_rate} SF/year`,
        })
      }
    if (
      filters.min_available_sf &&
      filters.max_available_sf &&
      fmtNumberAbbr(filters.min_available_sf) !==
        fmtNumberAbbr(filters.max_available_sf)
    ) {
      children.push({
        child: `${fmtNumberAbbr(filters.min_available_sf)} - ${fmtNumberAbbr(filters.max_available_sf)} SF`,
        content: `Available SF: ${filters.min_available_sf.toLocaleString()} - ${filters.max_available_sf.toLocaleString()} SF`,
      })
    } else if (filters.min_available_sf) {
      children.push({
        child: `${fmtNumberAbbr(filters.min_available_sf)} SF`,
        content: `Available SF: ${filters.min_available_sf.toLocaleString()} SF`,
      })
    }
    if (listing.suites && listing.suites.length > 1) {
      children.push({
        child: `${listing.suites.length} suites`,
      })
    }
  }

  if (filters.building_sf) {
    children.push({
      child: (
        <span className="inline-flex items-center gap-1">
          <LucideBuilding className="h-3.5 w-3.5 text-muted-foreground" />
          {`${fmtNumberAbbr(filters.building_sf)} SF`}
        </span>
      ),
      content: `Building SF: ${filters.building_sf.toLocaleString()} SF`,
    })
  }

  return (
    <div className="mt-2 flex flex-wrap gap-1">
      <Badge
        className={cn(
          'text-base',
          listing.type === 'sale' && 'bg-[#0284c7] hover:bg-[#0284c7]',
          listing.type === 'lease' && 'bg-[#c026d3] hover:bg-[#c026d3]',
          listing.type === 'sale/lease' && 'bg-[#4338ca] hover:bg-[#4338ca]'
        )}>
        {listing.status === 'active'
          ? TYPES[listing.type]
          : STATUSES[listing.status]}
      </Badge>
      {listing.labels &&
        listing.labels.map((label) => (
          <ClientLabelBadge key={label.id} label={label} />
        ))}
      {listing.property_types ? (
        listing.property_types.map((value, index) => {
          return (
            <Badge
              key={index}
              className={cn('text-base', getPropertyTypeClassName(value))}>
              <span>{value}</span>
            </Badge>
          )
        })
      ) : (
        <Badge className={cn('text-base', getPropertyTypeClassName('Other'))}>
          <span>Other</span>
        </Badge>
      )}

      {children.map(({ child, content }, index) => (
        <Badge key={index} variant="secondary" className="text-base">
          {content ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger>{child}</TooltipTrigger>
                <TooltipContent side="top" className="text-base">
                  {content}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            child
          )}
        </Badge>
      ))}
    </div>
  )
}
